/**
 * Shared Speech SDK Configuration Defaults
 *
 * Provides explicit values for all required AssessmentConfig fields and
 * sensible overrides for optional SDK properties where the Azure defaults
 * don't match app needs.
 *
 * Callers spread these defaults and add session-specific overrides:
 *   { ...ASSESSMENT_DEFAULTS, language: 'en-US', referenceText: '...' }
 *
 * The concrete type is preserved via `satisfies` so TypeScript can verify
 * that the spread result includes all required AssessmentConfig fields.
 */

import type { AssessmentConfig } from './NativeAzureSpeech';

export const ASSESSMENT_DEFAULTS = {
  // ── Required SDK fields (must always be set explicitly) ──
  gradingSystem: 'HundredMark', // SDK default: FivePoint (0-5) — we need 0-100
  granularity: 'Phoneme', // SDK default: Phoneme (same, but required by constructor)
  outputFormat: 'Detailed', // SDK default: Simple — must be Detailed for pronunciation scores
  enableMiscue: false, // SDK default: false — overridden per-call by AzureSpeech.ts
  enableProsodyAssessment: true, // SDK default: false — we want prosody scoring

  // ── Optional SDK overrides (where SDK defaults don't match app needs) ──
  enableTwoPassUnscriptedAssessment: true, // SDK default: false — improves unscripted accuracy
  phonemeAlphabet: 'SAPI', // SDK default: SAPI (same — explicit for clarity)
  initialSilenceTimeoutMs: 5000, // SDK default: ~15000 for continuous — too long
  requestWordLevelTimestamps: true, // SDK default: false — needed for word-level data
  profanityOption: 'raw', // SDK default: masked — we want unfiltered text
  textForm: 'lexical', // SDK default: Display ("30.") — LLM downstream compares against lexical reference text ("thirty")

  // ── App-level properties (not SDK settings — handled by native modules) ──
  // After speech is detected, auto-stop recording when no new Recognizing
  // partials arrive for this duration. Timer resets on every partial, so
  // mid-sentence pauses won't trigger it — only true end-of-turn silence.
  // ConversationSpeech may override with a longer value for free-form talk.
  endOfTurnSilenceMs: 3000,
  // Failsafe: if stopContinuousRecognition() hangs and the SDK never fires
  // Canceled(EndOfStream), force-signal the semaphore/latch after this duration
  // so the promise resolves. Returns whatever Recognized segments accumulated
  // up to that point (real data, not empty). Logs A16:StopTimeout anomaly.
  stopSafetyTimeoutMs: 8000,
  logRN: __DEV__, // JS-layer logging (AzureSpeech.ts, ConversationSpeech.ts)
  logSDK: __DEV__, // Native/SDK-layer logging (Kotlin, Swift, web SDK)
  iosSampleRate: 16000, // AVAudioSession preferred sample rate
  iosBufferDuration: 0.02, // AVAudioSession preferred IO buffer duration (seconds)
} satisfies Partial<AssessmentConfig>;

/**
 * Initial-silence budget for scripted assessment (reference text on screen).
 * Users read the phrase after recording starts — the 5s default aborts
 * scripted sessions with NoMatch while they're still reading.
 */
export const SCRIPTED_INITIAL_SILENCE_MS = 10000;
